<template>
  <v-dialog :model-value="modelValue" max-width="600" @update:model-value="$emit('update:modelValue', $event)">
    <v-card>
      <v-card-title class="text-h6">{{ mode === "edit" ? "Edit Patient" : "Register New Patient" }}</v-card-title>
      <v-card-text>
        <v-form ref="formRef" validate-on="eager">
          <v-row dense>
            <v-col cols="6">
              <v-text-field
                v-model="form.firstName"
                label="First Name"
                :rules="patientNameRules"
                :disabled="mode === 'edit' && readOnlyPatientFields.includes('firstName')" />
            </v-col>
            <v-col cols="6">
              <v-text-field
                v-model="form.lastName"
                label="Last Name"
                :rules="patientNameRules"
                :disabled="mode === 'edit' && readOnlyPatientFields.includes('lastName')" />
            </v-col>
            <v-col cols="6">
              <v-text-field
                v-model="form.middleName"
                label="Middle Name"
                :disabled="mode === 'edit' && readOnlyPatientFields.includes('middleName')" />
            </v-col>
            <v-col cols="6">
              <v-text-field v-model="form.suffix" label="Suffix" />
            </v-col>
            <v-col cols="6">
              <v-text-field
                v-model="form.phoneNumber"
                label="Phone Number"
                :rules="patientPhoneRequired ? [requiredRule, patientPhoneLengthRule] : [patientPhoneLengthRule]" />
            </v-col>
            <v-col cols="6">
              <text-date-input
                v-model="form.dob"
                label="Date of Birth"
                :rules="patientDobRequired ? [requiredRule] : []" />
            </v-col>
            <v-col v-if="showStatusField && mode === 'edit' && form.status" cols="6">
              <v-text-field v-model="form.status" label="Status" :disabled="true" />
            </v-col>
            <v-col v-if="showExternalIdField && mode === 'edit'" cols="6">
              <v-text-field
                v-model="form.externalId"
                label="External ID"
                :disabled="originalExternalId !== '' && originalExternalId != null" />
            </v-col>
          </v-row>
          <v-alert
            v-if="showExternalIdField && duplicateExternalIdCount > 0"
            type="error"
            density="compact"
            class="mt-2">
            {{ duplicateExternalIdCount }} patient(s) found with the same External ID. Cannot save.
          </v-alert>
          <v-alert
            v-if="showExternalIdField && mode === 'edit' && !originalExternalId && form.externalId"
            type="warning"
            density="compact"
            variant="tonal"
            class="mt-2">
            <v-icon>mdi-alert</v-icon> If an incorrect External ID is entered, the wrong upstream EHR patient record
            might be impacted.
          </v-alert>
        </v-form>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn-lh variant="text" @click="close">Cancel</v-btn-lh>
        <v-btn-lh color="primary" :disabled="duplicateExternalIdCount > 0" @click="onSave">
          {{ mode === "edit" ? "Save Changes" : "Save New Patient" }}
        </v-btn-lh>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { ref, computed, watch, onUnmounted } from "vue";
import { patientService } from "@/services/patientService";
import { useRequirementsStore } from "@/stores/requirementsStore";
import { requiredRule, lengthRule } from "@/utils/form";
import { getPatientNameRulesForClient } from "@/clientSpecific/base/intakeUI";

const requirementsStore = useRequirementsStore();
const patientNameRules = ref([requiredRule]);

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
  patient: {
    type: Object,
    default: null,
  },
  mode: {
    type: String,
    default: "edit",
    validator: (v) => ["edit", "create"].includes(v),
  },
  clientCompany: {
    type: Number,
    required: true,
  },
});

const emit = defineEmits(["update:modelValue", "save", "create"]);

const isNewPatient = computed(() => !props.patient?.externalId);

watch(
  [() => requirementsStore.clientConfig, isNewPatient],
  async ([config, newPatientFlag]) => {
    patientNameRules.value = config ? await getPatientNameRulesForClient(config, newPatientFlag) : [requiredRule];
  },
  { immediate: true }
);

const formRef = ref(null);
const form = ref({});
const originalExternalId = ref(null);
const originalPatient = ref(null);
const duplicateExternalIdCount = ref(0);
let externalIdCheckTimeout = null;

const patientDobRequired = computed(
  () => requirementsStore.clientConfig?.intake?.requiredFields?.includes("patientDob") || false
);
const patientPhoneRequired = computed(
  () => requirementsStore.clientConfig?.intake?.requiredFields?.includes("patientPhone") || false
);
const showStatusField = computed(
  () => requirementsStore.clientConfig?.intake?.extraFields?.includes("patient.status") || false
);
const showExternalIdField = computed(
  () => requirementsStore.clientConfig?.intake?.extraFields?.includes("patient.externalId") || false
);
const readOnlyPatientFields = computed(() => requirementsStore.clientConfig?.intake?.readOnlyPatientFields ?? []);

onUnmounted(() => {
  if (externalIdCheckTimeout) {
    clearTimeout(externalIdCheckTimeout);
  }
});

const _phoneNumberLengthRuleFn = lengthRule(10, "Phone number must be 10 digits");
const patientPhoneLengthRule = (value) => _phoneNumberLengthRuleFn(value?.replace(/\D/g, ""));

// Initialize form when dialog opens
watch(
  () => props.modelValue,
  (isOpen) => {
    if (isOpen && props.patient) {
      form.value = {
        firstName: props.patient.firstName || "",
        lastName: props.patient.lastName || "",
        middleName: props.patient.middleName || "",
        suffix: props.patient.suffix || "",
        phoneNumber: props.patient.phoneNumber || "",
        dob: props.patient.dob || null,
        externalId: props.patient.externalId || "",
        status: props.patient.extraData?.status || "",
      };
      originalExternalId.value = props.patient.externalId || "";
      originalPatient.value = { ...form.value };
      duplicateExternalIdCount.value = 0;
    } else if (isOpen && !props.patient) {
      // Create mode with no defaults
      form.value = {
        firstName: "",
        lastName: "",
        middleName: "",
        suffix: "",
        phoneNumber: "",
        dob: null,
        externalId: "",
      };
      originalExternalId.value = null;
      originalPatient.value = null;
      duplicateExternalIdCount.value = 0;
    }
  }
);

// External ID duplicate check
const checkDuplicateExternalId = async (externalId) => {
  if (!externalId || externalId === originalExternalId.value) {
    duplicateExternalIdCount.value = 0;
    return;
  }
  try {
    const response = await patientService.getByExternalId(externalId);
    duplicateExternalIdCount.value = response?.count || 0;
  } catch {
    duplicateExternalIdCount.value = 0;
  }
};

watch(
  () => form.value.externalId,
  (newExternalId) => {
    if (externalIdCheckTimeout) {
      clearTimeout(externalIdCheckTimeout);
    }
    externalIdCheckTimeout = setTimeout(() => {
      checkDuplicateExternalId(newExternalId);
    }, 500);
  }
);

const close = () => {
  emit("update:modelValue", false);
};

const onSave = async () => {
  if (!formRef.value) return;
  const validation = await formRef.value.validate();
  if (!validation.valid) return;

  if (props.mode === "edit") {
    // Compute changed fields
    const changedFields = {};
    for (const key of Object.keys(form.value)) {
      if (form.value[key] !== originalPatient.value[key]) {
        changedFields[key] = form.value[key];
      }
    }
    if (Object.keys(changedFields).length > 0) {
      emit("save", props.patient.id, changedFields);
    }
    close();
  } else {
    emit("create", { ...form.value, clientCompany: props.clientCompany });
    close();
  }
};
</script>
